/**
 * The standard Blume theme switch (kit docs/consuming.md): flip
 * `data-theme` on the document root inside a View Transition so the whole
 * page cross-fades, with a class-based fallback and reduced-motion respect.
 * `@blumemethod/styles` ships the CSS; this is the app-side toggle.
 *
 * The choice persists in localStorage (`blume-theme`, same key as the
 * marketing site): `initTheme()` runs before bootstrap in `main.ts` so the
 * stored theme lands before first paint - no flash of the wrong theme.
 */
import { Capacitor } from '@capacitor/core';
import { signal } from '@angular/core';

const STORAGE_KEY = 'blume-theme';

/**
 * The theme in force, as a signal.
 *
 * It exists because there are now two surfaces showing it - the More sheet's
 * row and the Settings switch - and each keeping its own copy, seeded from the
 * DOM when it happened to be constructed, means toggling from one leaves the
 * other saying the opposite. The document attribute is still the source of
 * truth for the STYLES; this mirrors it for anything that has to render the
 * state rather than be styled by it.
 *
 * A plain module-level signal, not a service: `initTheme()` runs before
 * bootstrap in main.ts, so there is no injector yet.
 */
const _theme = signal<'light' | 'dark'>('dark');

/** The theme in force. Read it; call {@link toggleTheme} to change it. */
export const currentTheme = _theme.asReadonly();

/**
 * Mirror the app theme onto the NATIVE keyboard (iOS): dark theme, dark
 * keyboard. Lazy-imports the Keyboard plugin and no-ops on the web, the same
 * pattern as the kit's haptics.
 */
function syncNativeKeyboard(theme: 'light' | 'dark'): void {
  if (!Capacitor.isNativePlatform()) return;
  void import('@capacitor/keyboard')
    .then(({ Keyboard, KeyboardStyle }) =>
      Keyboard.setStyle({
        style: theme === 'dark' ? KeyboardStyle.Dark : KeyboardStyle.Light,
      }),
    )
    .catch(() => undefined); // keyboard styling is a nicety, never an error
}

/** Apply the stored theme (if any) - called once, before bootstrap. */
export function initTheme(): void {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      document.documentElement.setAttribute('data-theme', stored);
    }
  } catch {
    // Storage unavailable (private mode etc.) - dark default stands.
  }
  // The keyboard follows the EFFECTIVE theme (the app defaults to dark).
  const effective = stored === 'light' ? 'light' : 'dark';
  _theme.set(effective);
  syncNativeKeyboard(effective);
}

export function toggleTheme(): void {
  const root = document.documentElement;
  const next = root.getAttribute('data-theme') === 'light' ? 'dark' : 'light';
  const flip = () => {
    root.setAttribute('data-theme', next);
    _theme.set(next);
    syncNativeKeyboard(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Storage unavailable - the switch still works for this session.
    }
  };

  if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
    flip();
    return;
  }

  // An open kit overlay (dialog/sheet - all compose BlumeDialog's
  // .blume-overlay) rules the View Transition out: a transition renders the
  // page from snapshots, and a snapshot cannot reproduce a top-layer
  // dialog's blurred ::backdrop (backdrop-filter samples the LIVE content
  // behind it) - on iOS WebKit the blur visibly drops for the crossfade and
  // snaps back after. The .theme-anim path keeps the DOM live, so the blur
  // holds while the colors beneath it transition.
  const overlayOpen = document.querySelector('.blume-overlay[open]') !== null;

  if (document.startViewTransition && !overlayOpen) {
    root.classList.add('theme-switching'); // freeze per-element transitions
    let transition: ViewTransition;
    try {
      transition = document.startViewTransition(flip);
    } catch {
      // startViewTransition can throw synchronously (e.g. document hidden, or
      // a transition already mid-flight) - fall back to a plain flip.
      flip();
      root.classList.remove('theme-switching');
      return;
    }
    // An interrupted transition rejects any of `updateCallbackDone` / `ready` /
    // `finished` - swallow all three so the global error listener stays quiet
    // (a fast double-toggle skips the in-flight one, which is expected).
    transition.updateCallbackDone.catch(() => undefined);
    transition.ready.catch(() => undefined);
    transition.finished
      .catch(() => undefined)
      .finally(() => root.classList.remove('theme-switching'));
  } else {
    root.classList.add('theme-anim'); // fallback crossfade
    flip();
    setTimeout(() => root.classList.remove('theme-anim'), 450);
  }
}
